import type { CycleEntry, ProfilingState } from "./types";
import type { Trace, TraceEvent } from "../tracing/types";
import { tracesInWindow } from "../tracing/selectors";
import { aggregateCycles, groupSummaries, type AggregateRow, type GroupSummary } from "./selectors";

/** Sentinel for `fileName === undefined` — matches the "(inline)" label the UI already shows. */
export const INLINE_FILE = "(inline)";

/** An event identity to filter passes by: the trace kind plus the (optional) subject name. */
export type EventOption = { kind: TraceEvent["kind"]; name?: string };

/** Stable identity for an event option (":" is safe — kinds are fixed identifiers). */
export const eventOptionKey = (o: EventOption): string => `${o.kind}:${o.name ?? ""}`;

const traceEventOption = (t: Trace): EventOption => ({ kind: t.event.kind, name: t.event.name });

/**
 * Inclusive wall-time bounds (ms) on a pass's elapsed time: only `min` ⇒ "≥ min", only `max` ⇒
 * "≤ max", both ⇒ "between".  `null` means unbounded on that side.
 */
export type WallTimeFilter = { min: number | null; max: number | null };

const wallTimeActive = (w: WallTimeFilter): boolean => w.min != null || w.max != null;

const wallTimeMatches = (w: WallTimeFilter, wallTime: number): boolean =>
    (w.min == null || wallTime >= w.min) && (w.max == null || wallTime <= w.max);

/**
 * The Profiling tab's filters.  Values within one filter are OR'd, the filters are AND'd; an empty
 * array (or all-null wall-time bounds) means "no constraint".
 */
export type ProfilingFilters = {
    events: EventOption[];
    /** `fileName` values; `INLINE_FILE` stands in for cycles without one. */
    files: string[];
    /** Exact `ProfilerNodeTiming.source` strings (verbatim parser output — never normalized). */
    sources: string[];
    /** Bounds on the pass's wall-clock span (`GroupSummary.wallTime`, ms). */
    wallTime: WallTimeFilter;
};

export const EMPTY_FILTERS: ProfilingFilters = { events: [], files: [], sources: [], wallTime: { min: null, max: null } };

export const hasActiveFilters = (f: ProfilingFilters): boolean =>
    f.events.length > 0 || f.files.length > 0 || f.sources.length > 0 || wallTimeActive(f.wallTime);

/**
 * Whether filtering results can depend on the trace log at all — only the event filter reads it
 * (wall-time/file/source filtering never does).  Consumers use this to keep trace churn (a new
 * trace per canvas interaction while profiling is live) out of their memo dependencies.
 */
export const filtersNeedTraces = (f: ProfilingFilters): boolean => f.events.length > 0;

export type GroupWindow = { fromPerf: number; toPerf: number };

/**
 * The causal trace window of each render pass, keyed by `rootCycleId`: a pass starting at `T`,
 * preceded by a pass starting at `T_prev`, is attributed the traces with `perfTime ∈ (T_prev, T]`.
 * The oldest pass's window opens at `floorPerf` (the profiling start — `startedAtPerf` — so events
 * preceding a mid-session enable or a Clear are never cited; `-Infinity` means no floor).  Input
 * order does not matter.
 */
export function groupEventWindows(groups: GroupSummary[], floorPerf = -Infinity): Map<string, GroupWindow> {
    const sorted = groups.toSorted((a, b) => a.startTime - b.startTime);
    const windows = new Map<string, GroupWindow>();
    sorted.forEach((g, i) => windows.set(g.rootCycleId, {
        fromPerf: i > 0 ? sorted[i - 1].startTime : floorPerf,
        toPerf: g.startTime,
    }));
    return windows;
}

// Note: the trace ring buffer (`maxTraces`) can trim traces out of an old pass's window, at which
// point that pass simply stops matching event filters.
function groupMatchesEvents(win: GroupWindow | undefined, traces: Trace[], eventKeys: Set<string>): boolean {
    if (!win) return false;
    return tracesInWindow(traces, win.fromPerf, win.toPerf)
        .some(t => eventKeys.has(eventOptionKey(traceEventOption(t))));
}

// A pass matches if ANY of its cycles (root Canvas or nested Template) rendered a matching file.
function groupMatchesFiles(profiling: ProfilingState, group: GroupSummary, files: Set<string>): boolean {
    return group.cycleIds.some(id => {
        const cycle = profiling.cyclesById[id];
        return cycle != null && files.has(cycle.fileName ?? INLINE_FILE);
    });
}

const cycleBatches = (cycle: CycleEntry) => [
    ...(cycle.render ? [cycle.render] : []),
    ...cycle.callbacks,
    ...(cycle.react ? [cycle.react] : []),
];

// Exact string comparison: sources come verbatim from the parser, so normalizing whitespace could
// only conflate distinct nodes.  (A TemplateUpdated push changes source text, so a filter captured
// before the edit won't match post-edit passes — correct, the old node no longer exists.)
function groupMatchesSources(profiling: ProfilingState, group: GroupSummary, sources: Set<string>): boolean {
    return group.cycleIds.some(id => {
        const cycle = profiling.cyclesById[id];
        return cycle != null && cycleBatches(cycle).some(b => b.nodes.some(n => sources.has(n.source)));
    });
}

/**
 * The render passes (input order preserved) satisfying ALL active filters.  Returns the input array
 * reference when no filters are active, so downstream memos stay stable.
 */
export function filterGroups(
    groups: GroupSummary[],
    profiling: ProfilingState,
    traces: Trace[],
    filters: ProfilingFilters,
): GroupSummary[] {
    if (!hasActiveFilters(filters)) return groups;
    const eventKeys = new Set(filters.events.map(eventOptionKey));
    const files = new Set(filters.files);
    const sources = new Set(filters.sources);
    const windows = eventKeys.size ? groupEventWindows(groups, profiling.startedAtPerf ?? -Infinity) : undefined;
    return groups.filter(g =>
        (eventKeys.size === 0 || groupMatchesEvents(windows!.get(g.rootCycleId), traces, eventKeys)) &&
        (files.size === 0 || groupMatchesFiles(profiling, g, files)) &&
        (sources.size === 0 || groupMatchesSources(profiling, g, sources)) &&
        wallTimeMatches(filters.wallTime, g.wallTime));
}

/**
 * Aggregate rows under the active filters:
 * - `events` and `wallTime` are pass-level: they restrict which PASSES feed the aggregation
 *   (cycles of non-matching passes excluded),
 * - `files` restrict ROWS by `row.fileName` (row-level: a matching pass can still contain other
 *   templates whose rows must not appear),
 * - `sources` restrict ROWS by exact `row.source`.
 */
export function filteredAggregateRows(
    profiling: ProfilingState,
    traces: Trace[],
    filters: ProfilingFilters,
): AggregateRow[] {
    let cycles: CycleEntry[];
    if (filters.events.length > 0 || wallTimeActive(filters.wallTime)) {
        const matching = filterGroups(groupSummaries(profiling), profiling, traces,
            { ...EMPTY_FILTERS, events: filters.events, wallTime: filters.wallTime });
        cycles = matching.flatMap(g => g.cycleIds.map(id => profiling.cyclesById[id]).filter(Boolean));
    } else {
        cycles = Object.values(profiling.cyclesById);
    }

    let rows = aggregateCycles(cycles);
    if (filters.files.length) {
        const files = new Set(filters.files);
        rows = rows.filter(r => files.has(r.fileName ?? INLINE_FILE));
    }
    if (filters.sources.length) {
        const sources = new Set(filters.sources);
        rows = rows.filter(r => sources.has(r.source));
    }
    return rows;
}

/**
 * The cycles (template renders) that individually match the template and/or node-source filter
 * values — used to highlight the contributing templates in the Passes tree.  A cycle matches when
 * it rendered a filtered template OR contains a filtered node source (union, because the highlight
 * answers "which templates make this pass match?").  Returns null when neither filter is active.
 * The event filter is deliberately excluded: events attribute whole passes, not single templates.
 */
export function matchingCycleIds(profiling: ProfilingState, filters: ProfilingFilters): Set<string> | null {
    if (filters.files.length === 0 && filters.sources.length === 0) return null;
    const files = new Set(filters.files);
    const sources = new Set(filters.sources);
    const ids = new Set<string>();
    for (const cycle of Object.values(profiling.cyclesById)) {
        const fileMatch = files.size > 0 && files.has(cycle.fileName ?? INLINE_FILE);
        const sourceMatch = !fileMatch && sources.size > 0 &&
            cycleBatches(cycle).some(b => b.nodes.some(n => sources.has(n.source)));
        if (fileMatch || sourceMatch) ids.add(cycle.cycleId);
    }
    return ids;
}

/** Distinct event (kind, name) pairs among the traces attributable to any retained pass. */
export function eventFilterOptions(groups: GroupSummary[], traces: Trace[], floorPerf = -Infinity): EventOption[] {
    const windows = [...groupEventWindows(groups, floorPerf).values()];
    const seen = new Map<string, EventOption>();
    for (const t of traces) {
        if (!windows.some(w => t.perfTime > w.fromPerf && t.perfTime <= w.toPerf)) continue;
        const option = traceEventOption(t);
        seen.set(eventOptionKey(option), option);
    }
    return [...seen.values()];
}

/** Distinct template paths across retained cycles (`INLINE_FILE` for undefined), sorted. */
export function fileFilterOptions(profiling: ProfilingState): string[] {
    return [...new Set(Object.values(profiling.cyclesById).map(c => c.fileName ?? INLINE_FILE))].sort();
}
